import type { QueryClient } from '@tanstack/react-query';
import { resetViewerSmetaResolutionSession } from '@/shared/api/viewer-smeta-core';
import { resetDecorScans } from '@/features/scene/lib/decor-scan';
import { advanceAuthenticatedCacheGeneration, authenticatedCacheGeneration } from '@/shared/lib/authenticated-session';
export { authenticatedCacheGeneration } from '@/shared/lib/authenticated-session';

const LEGACY_KEY = 'holzcom-rq-cache';
let unsubscribe: (() => void) | undefined;
let activeKey: string | undefined;
let pending: Promise<void> = Promise.resolve();

function cacheKey(scope: string): string {
  return `${LEGACY_KEY}:${scope}`;
}

export async function activateAuthenticatedCache(queryClient: QueryClient, scope: string): Promise<void> {
  const ownGeneration = advanceAuthenticatedCacheGeneration();
  unsubscribe?.();
  unsubscribe = undefined;
  const key = cacheKey(scope);
  if (activeKey !== key) resetViewerSmetaResolutionSession();
  pending = pending.catch(() => undefined).then(async () => {
    const [{ persistQueryClient }, { createAsyncStoragePersister }, { get, set, del }] = await Promise.all([
      import('@tanstack/react-query-persist-client'),
      import('@tanstack/query-async-storage-persister'),
      import('idb-keyval'),
    ]);
    await del(LEGACY_KEY);
    if (ownGeneration !== authenticatedCacheGeneration()) return;
    const persister = createAsyncStoragePersister({
      storage: {
        getItem: (storageKey) => get(storageKey) as Promise<string | null>,
        setItem: (storageKey, value) => ownGeneration === authenticatedCacheGeneration()
          ? set(storageKey, value)
          : Promise.resolve(),
        removeItem: (storageKey) => del(storageKey),
      },
      key,
      throttleTime: 1000,
    });
    const [stop, restored] = persistQueryClient({
      queryClient,
      persister,
      maxAge: 24 * 60 * 60 * 1000,
      buster: 'auth-v3',
      dehydrateOptions: {
        shouldDehydrateQuery: (q) => {
          if (q.state.status !== 'success') return false;
          const k0 = String((q.queryKey as unknown[])[0] ?? '');
          return ![
            'cabinet-templates',
            'analysis',
            'validation',
            'estimate',
            'hardware-purchase',
            'source-verification',
            'source-verification-readiness',
            'source-verification-job',
          ].includes(k0);
        },
      },
    });
    await restored;
    if (ownGeneration !== authenticatedCacheGeneration()) {
      stop();
      return;
    }
    unsubscribe = stop;
    activeKey = key;
  });
  await pending;
}

export async function clearAuthenticatedCache(queryClient: QueryClient): Promise<void> {
  advanceAuthenticatedCacheGeneration();
  resetViewerSmetaResolutionSession();
  // Logout/revoke wipes the per-user runtime decor registry: user B must not
  // inherit user A's uploaded scans. Idempotent and synchronous — runs on every
  // duplicate 401/logout signal exactly like the rest of this cleanup.
  resetDecorScans();
  unsubscribe?.();
  unsubscribe = undefined;
  const keyToRemove = activeKey;
  activeKey = undefined;
  await pending.catch(() => undefined);
  await queryClient.cancelQueries();
  queryClient.clear();
  try {
    const { del, keys } = await import('idb-keyval');
    const persistedKeys = await keys();
    const protectedKeys = persistedKeys.filter(
      (key): key is IDBValidKey => typeof key === 'string' && key.startsWith(`${LEGACY_KEY}:`),
    );
    await Promise.all([
      del(LEGACY_KEY),
      ...protectedKeys.map((key) => del(key)),
      ...(keyToRemove ? [del(keyToRemove)] : []),
    ]);
  } catch {
    // IndexedDB can be unavailable in private/locked-down contexts. Memory is
    // already cleared; never let storage failure reopen or strand the gate.
  }
}
